import { formatDate } from '@branch-messenger/willow-ui/utils'
import {
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import { isWithinInterval, parseISO } from 'date-fns'
import { useMemo } from 'react'

import { centsToDollar, dollarToCents } from '@/lib/currency'
import { getValues } from '@/lib/data-helper'
import { getDateRangeFromPreset } from '@/lib/filter-helpers'
import {
  advanceQuoteQueryOptions,
  advancesQueryOptions,
  ewaBranchVerificationQueryOptions,
  ewaPayrollScheduleQueryOptions,
  reportedEarningsQueryOptions,
  reportedHoursQueryOptions,
} from '@/queries/ewa'
import {
  AdvanceActivityParams,
  AdvanceQuote,
  BranchVerification,
} from '@/types/ewa'

export const selectVerificationData = (data?: BranchVerification) => {
  const availableAdvanceAmount = data?.payout_limits?.daily_limit ?? 0
  const limitReached = data?.payout_limits?.period_max_reached ?? false
  const nextPayDay = data?.balance.info.next_pay_day ?? ''

  const minimumDefault = import.meta.env.VITE_ADVANCE_MINIMUM
  const minAmountRaw = minimumDefault || (data?.payout_limits?.minimum ?? 0)
  const maxAmountRaw = data?.payout_limits?.daily_limit ?? 0

  const minAdvanceAmount = centsToDollar(minAmountRaw)
  const maxAdvanceAmount = centsToDollar(maxAmountRaw)

  const nextPayRollDeadline = data?.balance?.pay_info?.next_payroll_deadline
  const prevPayRollDeadline = data?.balance?.pay_info?.prev_payroll_deadline

  const isInvalid =
    (data &&
      getValues<BranchVerification>(data).some(value => {
        if (!value || !('requirements' in value)) return false

        return value.requirements.some(requirement => {
          if ('items' in requirement) {
            return requirement.items.some(
              item => item.name !== 'card_verified' && !item.verified
            )
          }
          return !requirement.verified
        })
      })) ||
    limitReached

  return {
    isInvalid,
    minAmountRaw,
    maxAmountRaw,
    minAdvanceAmount,
    maxAdvanceAmount,
    limitReached,
    availableAdvanceAmount,
    nextPayDay,
    nextPayRollDeadline,
    prevPayRollDeadline,
  }
}

export const useBranchVerification = () => {
  return useSuspenseQuery({
    ...ewaBranchVerificationQueryOptions,
    select: selectVerificationData,
  })
}

export const useCurrentPayPeriodDeadline = () => {
  return useSuspenseQuery({
    ...ewaPayrollScheduleQueryOptions,
    select: data => data?.current?.payroll_deadline,
  })
}

export const useMappedAdvances = () => {
  const { data: currentPayRollPayDate } = useCurrentPayPeriodDeadline()
  const searchParams = useSearch({
    from: '/_authenticated/account/_dashboard/onDemandPay/',
  })

  const { fromString, toString } = useMemo(() => {
    return getDateRangeFromPreset({
      from: searchParams?.fromDate,
      to: searchParams?.toDate,
      presetId: searchParams?.datePreset,
    })
  }, [searchParams])

  const params: AdvanceActivityParams | undefined =
    fromString && toString ? { start: fromString, end: toString } : undefined

  return useSuspenseInfiniteQuery({
    ...advancesQueryOptions(params),
    select: data => {
      return data.pages.flatMap(page => {
        const isFirstPage = page.page_number === 1

        const mappedData = page.content
          .map((item, index) => {
            const isWithinCurrentPayPeriod =
              isFirstPage &&
              index === 0 &&
              isWithinInterval(parseISO(currentPayRollPayDate || ''), {
                start: parseISO(item.payroll_period?.start_date || ''),
                end: parseISO(item.payroll_period?.end_date || ''),
              })

            const filteredTransactions = item.transactions.filter(
              ({ status }) =>
                status === 'outstanding' ||
                status === 'pb paid' ||
                status === 'po pending'
            )

            return {
              ...item,
              isWithinCurrentPayPeriod,
              transactions: filteredTransactions,
              totalAdvanced: filteredTransactions.reduce(
                (acc, transaction) => acc + transaction.amount,
                0
              ),
            }
          })
          .filter(item => item.transactions.length > 0)
        return mappedData
      })
    },
  })
}

export const useMappedReportedEarnings = () => {
  return useSuspenseInfiniteQuery({
    ...reportedEarningsQueryOptions,
    select: data => data.pages.flatMap(page => page.content),
  })
}

export const useMappedReportedHours = () => {
  const {
    data: { prevPayRollDeadline, nextPayRollDeadline },
  } = useBranchVerification()

  return useSuspenseInfiniteQuery({
    ...reportedHoursQueryOptions({
      start_time: formatDate({
        date: prevPayRollDeadline!,
        outputFormat: `yyyy-MM-dd'T'HH:mm:ss`,
      }),
      end_time: formatDate({
        date: nextPayRollDeadline!,
        outputFormat: `yyyy-MM-dd'T'HH:mm:ss`,
      }),
    }),
    // tracked times endpoint currently will only have one page of data, BE is working on a new api that will have proper paging
    select: data => data.pages.flatMap(page => page.content[0].tracked_times),
  })
}

export const selectAdvanceQuoteData = (data?: AdvanceQuote) => {
  const quoteAmountRaw = data?.quote_amount ?? 0
  const feeAmountRaw = data?.quote_fee ?? 0
  const advanceQuote = data?.advance_quote ?? ''

  const receivedAmount = centsToDollar(quoteAmountRaw - feeAmountRaw)
  const feeAmount = centsToDollar(feeAmountRaw)

  return {
    advanceQuote,
    quoteAmountRaw,
    feeAmountRaw,
    receivedAmount,
    feeAmount,
  }
}

export const useAdvanceQuote = (amount: string) => {
  const debouncedAdvanceAmount = useDebounce(+amount, 300)
  const options = advanceQuoteQueryOptions({
    requested_advance_amount: dollarToCents(debouncedAdvanceAmount),
  })
  return useQuery({
    ...options,
    select: selectAdvanceQuoteData,
  })
}
